import React, { useContext, useEffect, useRef, useState, lazy, Suspense } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { cartContext } from "./CartContext";
import { CheckOutContext } from "./CheckOutContext";
import ProductImageCarousel from "./ProductImageCouresel";
import Dialogs from "./Dialogs";
import RecentlyViewed from "./RecentlyViewed";
import WhatsAppButton from "./WhatsAppButton";
import MobileStickyBar from "./MobileStickyBar";
import ProductSkeleton from "./ProductSkeleton";
import { AddCartItems, FetchProducts, FetchProductById, GetCartItems } from "../Data";
import { FiCheckCircle, FiStar, FiUser, FiThumbsUp, FiTruck, FiShield, FiPackage } from "react-icons/fi";
import { FaMobileAlt } from "react-icons/fa";
import AdManager from './Ads/AdManager';
import config from '../config';
import { FEATURES, KENYA, UI_TEXT, IMAGES } from '../constants';
import { 
  isUserLoggedIn, 
  getCurrentUserId, 
  addToGuestCart, 
  getGuestCartCount 
} from '../utils/guestUser';

const ProductDetail = () => {
  // Support both old (productName/productID) and new (slug) URL formats
  const { productName: encodedProductName, productID, slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userID, setUserID] = useState(localStorage.getItem('userID') || null);
  const [quantity, setQuantity] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount, updateCartCount } = useContext(cartContext);
  const { GetCartItemsAsync } = useContext(cartContext);
  const { addItemTocheckOut, updateItemQuantity, isItemInCheckout, checkOutData } = useContext(CheckOutContext);
  const [showSuccessDialog, setSuccessDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(null);
  const [boxContent, setBoxContent] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  // Helper function to generate product URL
  const generateProductUrl = (productName, productId) => {
    const safeName = encodeURIComponent(productName.replace(/[^\w\s-]/g, '').trim());
    return `/product/${safeName}/${productId}`;
  };

  const [expandedDetails, setExpandedDetails] = useState(false);
  const [expandedKeyFeatures, setExpandedKeyFeatures] = useState(false);
  const [expandedSpecifications, setExpandedSpecifications] = useState(false);
  const [expandedBoxContent, setExpandedBoxContent] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [customerReviews] = useState([
    {
      id: 1,
      name: "Sarah Johnson",
      rating: 5,
      date: "2024-10-05",
      title: "Excellent product!",
      review: "This product exceeded my expectations. Great quality and fast delivery. Highly recommended!",
      verified: true,
      helpful: 24
    },
    {
      id: 2,
      name: "Michael Chen",
      rating: 4,
      date: "2024-10-02",
      title: "Good value for money",
      review: "Solid product with good features. The build quality is decent and it works as described. Minor issues with setup but overall satisfied.",
      verified: true,
      helpful: 18
    },
    {
      id: 3,
      name: "Emily Davis",
      rating: 5,
      date: "2024-09-28",
      title: "Love it!",
      review: "Perfect for what I needed. Easy to use and the quality is outstanding. Will definitely buy again.",
      verified: true,
      helpful: 31
    },
    {
      id: 4,
      name: "David Wilson",
      rating: 3,
      date: "2024-09-25",
      title: "Average product",
      review: "It's okay, does what it's supposed to do but nothing special. Could be better for the price point.",
      verified: false,
      helpful: 7
    },
    {
      id: 5,
      name: "Jennifer Martinez",
      rating: 4,
      date: "2024-09-20",
      title: "Good purchase",
      review: "Happy with this purchase. Good functionality and arrived on time. Would recommend to others.",
      verified: true,
      helpful: 15
    }
  ]);  const detailsRef = useRef(null);
  const keyFeaturesRef = useRef(null);
  const specificationsRef = useRef(null);
  const boxContentRef = useRef(null);

  // Check if content exceeds max height
  const [showDetailsToggle, setShowDetailsToggle] = useState(false);
  const [showKeyFeaturesToggle, setShowKeyFeaturesToggle] = useState(false);
  const [showSpecificationsToggle, setShowSpecificationsToggle] = useState(false);
  const [showBoxContentToggle, setShowBoxContentToggle] = useState(false);


  useEffect(() => {
    if (detailsRef.current) {
      setShowDetailsToggle(detailsRef.current.scrollHeight > 300);
    }
    if (keyFeaturesRef.current) {
      setShowKeyFeaturesToggle(keyFeaturesRef.current.scrollHeight > 300);
    }
    if (specificationsRef.current) {
      setShowSpecificationsToggle(specificationsRef.current.scrollHeight > 300);
    }
    if (boxContentRef.current) {
      setShowBoxContentToggle(boxContentRef.current.scrollHeight > 300);
    }
  }, [product]);

  useEffect(() => {
    if (!product) return;
  
    const savedProducts = JSON.parse(localStorage.getItem("recentlyViewed")) || [];
    const updatedProducts = savedProducts.filter(item => item.id !== product.productId);
  
    // Handle both old (imageUrl string) and new (imageUrls array) formats
    const productImage = product.imageUrls?.[0] || product.imageUrl;
    
    updatedProducts.unshift({
      id: product.productId,
      name: product.productName,
      image: productImage,
      price: product.price,
      rating: 4.3,
      reviews: 31088,
    });
  
    if (updatedProducts.length > 5) updatedProducts.pop();
    localStorage.setItem("recentlyViewed", JSON.stringify(updatedProducts));
    setRecentlyViewed(updatedProducts);
  }, [product]);

  useEffect(() => {
    console.log('ProductDetail - useParams:', { slug, productID, encodedProductName });
    // Fetch product based on slug (new) or productID (old)
    if (slug || productID) {
      console.log('Triggering fetchProductDetails with slug:', slug, 'productID:', productID);
      fetchProductDetails();
    } else {
      console.log('No slug or productID found in URL params');
    }
  }, [slug, productID]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let productData = null;

      // Try new slug-based API first
      if (slug) {
        try {
          const apiUrl = `${config.baseUrl}/api/Product/slug/${slug}`;
          console.log('Fetching product with slug API:', apiUrl);
          
          const response = await fetch(apiUrl);
          
          console.log('Slug API response status:', response.status);
          
          if (response.ok) {
            const data = await response.json();
            console.log('Slug API response data:', data);
            
            if (data.success && data.data) {
              productData = data.data;
              console.log('Product loaded successfully from slug API:', productData.productName);
            } else {
              console.log('Slug API returned unsuccessful or no data:', data);
            }
          } else {
            console.log('Slug API failed with status:', response.status);
            const errorText = await response.text();
            console.log('Error response:', errorText);
          }
        } catch (error) {
          console.error('Slug API error:', error);
          console.log('Slug API not available, trying productID');
        }
      } else {
        console.log('No slug parameter available');
      }
      
      // Fallback to old productID method if slug didn't work
      if (!productData && productID) {
        try {
          productData = await FetchProductById(productID);
        } catch (error) {
          console.log('Single product API not available, falling back to all products');
          const allProducts = await FetchProducts();
          productData = allProducts.find((p) => p.productId === productID);
        }
      }
      
      if (productData) {
        setProduct(productData);
        let parsedBoxContent;
        try {
          parsedBoxContent = JSON.parse(productData.box || '[]');
        } catch (error) {
          console.error("Failed to parse box content:", error);
          parsedBoxContent = [];
        }
        setBoxContent(Array.isArray(parsedBoxContent) ? parsedBoxContent : []);
      } else {
        setError("This product is no longer available");
      }
    } catch (error) {
      if (error.message === 'Product not found') {
        setError('This product is no longer available');
      } else {
        setError('Failed to load product details. Please check your connection and try again.');
      }
      console.error('Error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const parseKeyValuePairs = (data) => {
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        console.error('Failed to parse data:', error);
        return [];
      }
    }

    if (Array.isArray(data)) {
      return data
        .filter(item => item.includes(':') && !/^\d+\s*:/.test(item))
        .map(item => {
          const [key, value] = item.split(/:(.+)/);
          return [key.trim(), value ? value.trim() : ''];
        });
    } else if (typeof data === 'object' && data !== null) {
      return Object.entries(data).map(([key, value]) => [key.trim(), String(value).trim()]);
    }

    return [];
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <FiStar
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));
  };

  const getAverageRating = () => {
    const total = customerReviews.reduce((sum, review) => sum + review.rating, 0);
    return (total / customerReviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    customerReviews.forEach(review => {
      distribution[review.rating]++;
    });
    return distribution;
  };

  const handleAddToCart = async () => {
    if (!FEATURES.ENABLE_GUEST_CHECKOUT) {
      // Legacy behavior: require login
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/Login', { state: { from: location } });
        return;
      }
    }

    try {
      const checkoutItem = {
        productID: product.productId,
        productName: product.productName,
        price: product.price,
        quantity: quantity,
        productImage: product.imageUrls?.[0] || product.imageUrl,
        inStock: product.inStock !== false
      };

      if (isUserLoggedIn()) {
        // Logged in user: sync to backend
        const requestData = {
          applicationUserId: localStorage.getItem('userID'),
          productId: product.productId,
          quantity: quantity,
        };
        
        const response = await AddCartItems(requestData);

        if (response && response.success) {
          GetCartItemsAsync();
          addItemTocheckOut(checkoutItem);
          setSuccessDialog(true);
          setDialogMessage(response.message || 'Product added to cart successfully');
        } else if (response && response.errors && response.errors.length > 0) {
          setSuccessDialog(true);
          setDialogMessage(response.errors.join(', '));
        }
      } else {
        // Guest user: save locally
        addToGuestCart(checkoutItem);
        addItemTocheckOut(checkoutItem);
        
        // Update cart count display
        const guestCartCount = getGuestCartCount();
        updateCartCount(guestCartCount);
        
        setSuccessDialog(true);
        setDialogMessage('Product added to cart! Proceed to checkout to complete your order.');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      setSuccessDialog(true);
      setDialogMessage('Failed to add item to cart. Please try again.');
    }
  };

  const handleBuyNow = async () => {
    // First, add to cart (same flow as handleAddToCart)
    if (!FEATURES.ENABLE_GUEST_CHECKOUT) {
      // Legacy behavior: require login
      const token = localStorage.getItem('token');
      if (!token) {
        localStorage.setItem('buyNowProduct', JSON.stringify({
          productID: productID,
          quantity: quantity
        }));
        navigate('/Login', { state: { from: location, action: 'buyNow' } });
        return;
      }
    }

    try {
      const checkoutItem = {
        productID: product.productId,
        productName: product.productName,
        price: product.price,
        quantity: quantity,
        productImage: product.imageUrls?.[0] || product.imageUrl,
        inStock: product.inStock !== false,
        userId: getCurrentUserId(),
        isGuest: !isUserLoggedIn()
      };

      // Check if user is logged in
      if (isUserLoggedIn()) {
        // Logged-in user: save to backend
        const response = await AddCartItems(checkoutItem);
        if (response && response.message === 'Item Added to cart successfully') {
          addItemTocheckOut(checkoutItem);
          updateCartCount(response.totalItems || (checkOutData.length + 1));
        }
      } else {
        // Guest user: save locally
        addToGuestCart(checkoutItem);
        addItemTocheckOut(checkoutItem);
        
        // Update cart count display
        const guestCartCount = getGuestCartCount();
        updateCartCount(guestCartCount);
      }

      // Navigate to cart page (not directly to checkout)
      navigate('/Cart');
    } catch (error) {
      console.error('Error in Buy Now:', error);
      setSuccessDialog(true);
      setDialogMessage('Failed to add item to cart. Please try again.');
    }
  };

  const handleCloseDialog = async () => {
    setSuccessDialog(false);
    setDialogMessage(null);
  }

  const incrementQuantity = () => {
    const maxQuantity = Math.min(product?.stockQuantity || 1, 99);
    if (quantity < maxQuantity) {
      const newQuantity = quantity + 1;
      setQuantity(newQuantity);
      
      // Update checkout context if item already exists
      if (isItemInCheckout(product.productId)) {
        updateItemQuantity(product.productId, newQuantity);
      }
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      
      // Update checkout context if item already exists
      if (isItemInCheckout(product.productId)) {
        updateItemQuantity(product.productId, newQuantity);
      }
    }
  };

  // Handle Buy Now redirect from login
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isBuyNow = urlParams.get('buyNow');
    const urlQuantity = urlParams.get('quantity');
    
    if (isBuyNow === 'true' && product && urlQuantity) {
      const buyNowQuantity = parseInt(urlQuantity, 10);
      if (buyNowQuantity && buyNowQuantity > 0) {
        setQuantity(buyNowQuantity);
        // Auto-execute buy now after a short delay to ensure state is updated
        setTimeout(() => {
          handleBuyNow();
        }, 500);
        
        // Clean up URL
        const newUrl = location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [product, location.search]);

  if (loading) {
    return <ProductSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">😞</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <button 
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Try Again
            </button>
            <button 
              onClick={() => navigate('/')}
              className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find the product you're looking for. It may have been removed or is temporarily unavailable.</p>
          <div className="space-y-3">
            <button 
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Browse Products
            </button>
            <button 
              onClick={() => navigate(-1)}
              className="w-full bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Backend returns imageUrls as array, handle both old (imageUrl string) and new (imageUrls array) formats
  const productImages = product.imageUrls || (product.imageUrl ? JSON.parse(product.imageUrl) : []);
  const averageRating = getAverageRating();
  const ratingDistribution = getRatingDistribution();

  // Calculate prices for SEO
  const discount = (product.discount / 100) || 0;
  const discountedPrice = product.price * (1 - discount);
  const firstImage = productImages?.[0] || product.imageUrls?.[0] || '';
  
  // SEO: Canonical URL
  const canonicalUrl = product.slug 
    ? `https://quickcrate.co.ke/p/${product.slug}` 
    : `https://quickcrate.co.ke/product/${encodeURIComponent(product.productName)}/${product.productId}`;
  
  // SEO: Meta title
  const metaTitle = product.metaTitle || `${product.productName} | Buy in Kenya | Quickcrate`;
  
  // SEO: Meta description
  const metaDescription = product.metaDescription || 
    `Buy ${product.productName} in Kenya for KES ${discountedPrice.toLocaleString('en-KE')}. ` +
    `Fast delivery across Nairobi and Kenya. ${product.stockQuantity > 0 ? 'In stock now' : 'Pre-order available'} - Shop at Quickcrate.co.ke`;
  
  // SEO: Keywords
  const metaKeywords = product.metaKeywords || 
    `${product.productName}, buy in kenya, kenya electronics, ${product.brand || 'quickcrate'}, online shopping kenya`;

  // SEO: JSON-LD Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.productName,
    "image": Array.isArray(productImages) ? productImages : [firstImage],
    "description": product.description || metaDescription,
    "sku": product.sku || product.productId,
    "brand": {
      "@type": "Brand",
      "name": product.brand || "Quickcrate"
    },
    "offers": {
      "@type": "Offer",
      "url": canonicalUrl,
      "priceCurrency": "KES",
      "price": discountedPrice.toFixed(2),
      "availability": product.stockQuantity > 0 
        ? "https://schema.org/InStock" 
        : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Quickcrate"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": averageRating,
      "reviewCount": customerReviews.length
    }
  };

  return (
    <div className="product-detail-container bg-gray-50 min-h-screen">
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={metaKeywords} />
        
        {/* Preload first image for performance */}
        {firstImage && <link rel="preload" as="image" href={firstImage} />}
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={product.productName} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={firstImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Quickcrate" />
        <meta property="og:locale" content="en_KE" />
        <meta property="product:price:amount" content={discountedPrice.toFixed(2)} />
        <meta property="product:price:currency" content="KES" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={product.productName} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={firstImage} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {showSuccessDialog && (
        <Dialogs
          message={dialogMessage}
          type="cart"
          onClose={handleCloseDialog}
        />
      )}

      {/* Top Section - Product Overview */}
      <div className="bg-white p-6 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Side - Product Images */}
            <div className="lg:col-span-1">
              <div className="bg-white p-4 rounded-lg border">
                <ProductImageCarousel images={productImages} />
              </div>
            </div>

            {/* Middle - Product Info */}
            <div className="lg:col-span-1">
              <h1 className="text-2xl font-medium text-gray-900 mb-2">{product.productName}</h1>
              
              {/* Rating Section */}
              <div className="flex items-center mb-4">
                <div className="flex items-center">
                  {renderStars(Math.round(averageRating))}
                  <span className="ml-2 text-sm text-blue-600 hover:underline cursor-pointer">
                    {averageRating} out of 5 stars
                  </span>
                </div>
                <span className="ml-2 text-sm text-gray-600">
                  ({customerReviews.length} customer reviews)
                </span>
              </div>

              <hr className="mb-4" />

              {/* Price */}
              <div className="mb-4">
                {product.discount > 0 ? (
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl font-medium text-red-700">
                        KSH {Number(product.price * (1 - product.discount / 100)).toLocaleString()}
                      </span>
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                        -{product.discount}% OFF
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 line-through">
                      Was KSH {Number(product.price).toLocaleString()}
                    </div>
                  </div>
                ) : (
                  <span className="text-2xl font-medium text-red-700">
                    KSH {Number(product.price).toLocaleString()}
                  </span>
                )}
              </div>

              {/* Stock Status with Urgency */}
              <div className="mb-6">
                {product.inStock ? (
                  <div className="space-y-2">
                    <p className="text-lg text-green-700 font-medium flex items-center">
                      <FiPackage className="mr-2" />
                      In Stock - Ready to Ship
                    </p>
                    {/* Stock Urgency Message */}
                    {FEATURES.SHOW_STOCK_URGENCY && product.stockQuantity > 0 && product.stockQuantity < 5 && (
                      <p className="text-sm text-red-600 font-semibold animate-pulse">
                        ⚡ {UI_TEXT.STOCK_URGENCY.replace('{count}', product.stockQuantity)}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-lg text-red-700">Out of Stock</p>
                )}
              </div>

              {/* Kenya-specific Trust Signals */}
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <div className="flex items-start text-sm text-gray-700">
                  <FiTruck className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{UI_TEXT.PAY_ON_DELIVERY_NAIROBI}</p>
                    <p className="text-xs text-gray-600">
                      {KENYA.DELIVERY.NAIROBI_SAME_DAY} • Other areas: {KENYA.DELIVERY.OTHER_COUNTIES_DAYS}
                    </p>
                  </div>
                </div>
                <div className="flex items-start text-sm text-gray-700">
                  <FaMobileAlt className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="flex items-center">
                    <p className="font-medium mr-2">{UI_TEXT.MPESA_ACCEPTED}</p>
                    <img 
                      src={IMAGES.MPESA_LOGO} 
                      alt="M-Pesa" 
                      className="h-4 object-contain"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <FiShield className="text-green-600 mr-2 flex-shrink-0" />
                  <p className="font-medium">{UI_TEXT.SECURE_CHECKOUT}</p>
                </div>
                <div className="flex items-center text-sm text-gray-700">
                  <FiCheckCircle className="text-green-600 mr-2 flex-shrink-0" />
                  <p className="font-medium">{UI_TEXT.EASY_RETURNS}</p>
                </div>
              </div>

              {/* Product Description Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">About this item</h3>
                <ul className="space-y-1">
                  {product.productDescription.split("\n").slice(0, 4).map((feature, index) => (
                    feature.trim() && (
                      <li key={index} className="flex items-start text-sm">
                        <span className="text-gray-400 mr-2">•</span>
                        <span className="text-gray-700">{feature.trim()}</span>
                      </li>
                    )
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Side - Purchase Options */}
            <div className="lg:col-span-1">
              <div className="border rounded-lg p-4 bg-white">
                <div className="mb-4">
                  {product.discount > 0 ? (
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl font-medium text-gray-900">
                          KSH {Number(product.price * (1 - product.discount / 100)).toLocaleString()}
                        </span>
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                          -{product.discount}%
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 line-through">
                        Was KSH {Number(product.price).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <span className="text-2xl font-medium text-gray-900">
                      KSH {Number(product.price).toLocaleString()}
                    </span>
                  )}
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Quantity:</p>
                  <div className="flex items-center justify-center w-32 border border-gray-300 rounded-md overflow-hidden">
                    <button
                      onClick={decrementQuantity}
                      disabled={quantity <= 1}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-700 font-medium transition-colors duration-200 flex-shrink-0"
                    >
                      −
                    </button>
                    <span className="px-4 py-2 bg-white text-center font-medium text-gray-900 flex-1 border-l border-r border-gray-300">
                      {quantity}
                    </span>
                    <button
                      onClick={incrementQuantity}
                      disabled={quantity >= Math.min(product.stockQuantity, 99)}
                      className="px-3 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-700 font-medium transition-colors duration-200 flex-shrink-0"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Max: {Math.min(product.stockQuantity, 99)} available
                  </p>
                </div>

                <button 
                  onClick={handleAddToCart}
                  disabled={!product.inStock}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 disabled:bg-gray-300 text-black font-medium py-3 px-4 rounded-md mb-2 transition-colors duration-200"
                >
                  Add to Cart
                </button>

                <button 
                  onClick={handleBuyNow}
                  disabled={!product.inStock}
                  className="w-full bg-orange-400 hover:bg-orange-500 disabled:bg-gray-300 text-black font-medium py-3 px-4 rounded-md transition-colors duration-200"
                >
                  Buy Now
                </button>

                {/* Delivery and Payment Info */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-start text-sm text-gray-700">
                    <FiTruck className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Fast Delivery</p>
                      <p className="text-xs text-gray-600">
                        Nairobi: Same Day • Kenya: 1-3 Days
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start text-sm text-gray-700">
                    <FaMobileAlt className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium">M-Pesa Payment</p>
                      <p className="text-xs text-gray-600">
                        Paybill {KENYA.MPESA_PAYBILL} • Till Accepted
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <FiShield className="text-blue-600 mr-2 flex-shrink-0" />
                    <p className="font-medium">100% Secure Payment</p>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <FiCheckCircle className="text-green-600 mr-2 flex-shrink-0" />
                    <p className="font-medium">7 Days Return Policy</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Information Tabs */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8" aria-label="Tabs">
              {[
                { id: 'details', name: 'Product Details' },
                { id: 'specifications', name: 'Technical Details' },
                { id: 'reviews', name: 'Customer Reviews' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-yellow-500 text-yellow-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Product Details Tab */}
            {activeTab === 'details' && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Product Details</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-medium mb-3">Product Description</h3>
                    <div className="prose prose-sm max-w-none">
                      {product.productDescription ? (
                        product.productDescription.split("\n").map((line, index) => (
                          line.trim() && (
                            <p key={index} className="mb-2 text-gray-700">{line.trim()}</p>
                          )
                        ))
                      ) : (
                        <p className="text-gray-500">No description available</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-medium mb-3">Key Features</h3>
                    <div className="space-y-2">
                      {product.features || product.keyFeatures ? (
                        (product.features || product.keyFeatures).split("\n").map((feature, index) => (
                          feature.trim() && (
                            <div key={index} className="flex items-start">
                              <FiCheckCircle className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{feature.trim().replace(/^[•\-\*]\s*/, '')}</span>
                            </div>
                          )
                        ))
                      ) : (
                        <p className="text-gray-500">No features listed</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* What's in the Box Section */}
                <div className="mt-8">
                  <h3 className="font-medium mb-3">What's in the Box</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {product.boxContents ? (
                      <div className="space-y-2">
                        {product.boxContents.split("\n").map((item, index) => (
                          item.trim() && (
                            <div key={index} className="flex items-start">
                              <span className="text-blue-500 mr-2">📦</span>
                              <span className="text-gray-700">{item.trim().replace(/^[•\-\*]\s*/, '')}</span>
                            </div>
                          )
                        ))}
                      </div>
                    ) : boxContent.length > 0 ? (
                      <div className="space-y-2">
                        {boxContent.map((item, index) => (
                          <div key={index} className="flex items-start">
                            <span className="text-blue-500 mr-2">📦</span>
                            <span className="text-gray-700">{item.trim()}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500">Box contents not specified</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Technical Specifications Tab */}
            {activeTab === 'specifications' && (
              <div>
                <h2 className="text-xl font-semibold mb-4">Technical Details</h2>
                <div className="bg-gray-50 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <tbody className="divide-y divide-gray-200">
                      {/* Product Specifications from API */}
                      {product.productSpecification && (
                        <>
                          <tr className="bg-gray-100">
                            <td colSpan="2" className="px-6 py-3 text-sm font-semibold text-gray-900">
                              Product Specifications
                            </td>
                          </tr>
                          {product.productSpecification.split("\n").map((spec, index) => {
                            if (spec.includes(":")) {
                              const [key, ...valueParts] = spec.split(":");
                              const value = valueParts.join(":").trim();
                              return (
                                <tr key={`spec-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900 w-1/3">
                                    {key.trim()}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-700">
                                    {value || 'Not specified'}
                                  </td>
                                </tr>
                              );
                            }
                            return null;
                          }).filter(Boolean)}
                        </>
                      )}
                      
                      {/* Features as specifications if available */}
                      {(product.features || product.keyFeatures) && (
                        <>
                          <tr className="bg-gray-100">
                            <td colSpan="2" className="px-6 py-3 text-sm font-semibold text-gray-900">
                              Key Features
                            </td>
                          </tr>
                          {(product.features || product.keyFeatures).split("\n").map((feature, index) => {
                            const cleanFeature = feature.trim().replace(/^[•\-\*]\s*/, '');
                            if (cleanFeature) {
                              return (
                                <tr key={`feature-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-6 py-4 text-sm font-medium text-gray-900 w-1/3">
                                    {cleanFeature}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-700">
                                    ✓ Included
                                  </td>
                                </tr>
                              );
                            }
                            return null;
                          }).filter(Boolean)}
                        </>
                      )}

                      {/* Product Information */}
                      <tr className="bg-gray-100">
                        <td colSpan="2" className="px-6 py-3 text-sm font-semibold text-gray-900">
                          Product Information
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Product ID
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {product.productId}
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Category
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {product.categoryName || product.category?.name || 'General'}
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Sub Category
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {product.subCategoryName || product.subCategory?.name || 'Not specified'}
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Stock Quantity
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {product.stockQuantity} units available
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          SKU
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {product.sku || 'Not specified'}
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Product Type
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {product.productType || 'Physical'}
                        </td>
                      </tr>
                      <tr className="bg-white">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">
                          Status
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            product.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {product.status || 'Active'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Customer Reviews Tab */}
            {activeTab === 'reviews' && (
              <div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Rating Summary */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="text-lg font-semibold mb-4">Customer Reviews</h3>
                      <div className="flex items-center mb-4">
                        <div className="flex items-center">
                          {renderStars(Math.round(averageRating))}
                        </div>
                        <span className="ml-2 text-lg font-medium">{averageRating} out of 5</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">{customerReviews.length} global ratings</p>
                      
                      {/* Rating Distribution */}
                      <div className="space-y-2">
                        {[5, 4, 3, 2, 1].map(rating => {
                          const count = ratingDistribution[rating];
                          const percentage = ((count / customerReviews.length) * 100).toFixed(0);
                          return (
                            <div key={rating} className="flex items-center text-sm">
                              <span className="w-12">{rating} star</span>
                              <div className="flex-1 mx-3">
                                <div className="bg-gray-200 rounded-full h-4">
                                  <div 
                                    className="bg-yellow-400 h-4 rounded-full" 
                                    style={{width: `${percentage}%`}}
                                  ></div>
                                </div>
                              </div>
                              <span className="w-8 text-blue-600">{percentage}%</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Individual Reviews */}
                  <div className="lg:col-span-2">
                    <h3 className="text-lg font-semibold mb-4">Top Reviews</h3>
                    <div className="space-y-6">
                      {customerReviews.map((review) => (
                        <div key={review.id} className="border-b border-gray-200 pb-6">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                <FiUser className="w-5 h-5 text-gray-600" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center mb-1">
                                <span className="font-medium text-gray-900">{review.name}</span>
                                {review.verified && (
                                  <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                                    Verified Purchase
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center mb-2">
                                <div className="flex items-center">
                                  {renderStars(review.rating)}
                                </div>
                                <span className="ml-2 text-sm font-medium">{review.title}</span>
                              </div>
                              <p className="text-sm text-gray-600 mb-1">{review.date}</p>
                              <p className="text-gray-700 mb-3">{review.review}</p>
                              <div className="flex items-center text-sm text-gray-500">
                                <button className="flex items-center hover:text-gray-700">
                                  <FiThumbsUp className="w-4 h-4 mr-1" />
                                  Helpful ({review.helpful})
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product page advertisements */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <AdManager 
          location="product"
          context={{ 
            productName: product.productName,
            price: product.price,
            category: product.category,
            inStock: product.inStock
          }}
        />
      </div>

      {/* Recently Viewed Section */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        <RecentlyViewed />
      </div>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton 
        productName={product.productName}
        productUrl={canonicalUrl}
      />

      {/* Mobile Sticky Bottom Bar */}
      <MobileStickyBar
        price={product.price}
        discount={product.discount}
        onAddToCart={handleAddToCart}
        onBuyNow={handleBuyNow}
        inStock={product.inStock}
        quantity={quantity}
      />
    </div>
  );
};

export default ProductDetail;